// src/pages/Template/TemplateEditorPage.js
// Editor SVG (estável, do zero): load 1x + pan/zoom + seleção + box select
// drag/resize + layers + props + undo/redo + save sanitizado
//
// deps:
// npm i @svgdotjs/svg.js @svgdotjs/svg.select.js @svgdotjs/svg.resize.js @svgdotjs/svg.draggable.js dompurify

import React from "react";
import { useParams, useNavigate } from "react-router-dom";

import {
    Box,
    Stack,
    Button,
    Paper,
    Typography,
    CircularProgress,
    TextField,
    Divider,
    IconButton,
    Tooltip,
    Chip,
    Switch,
    FormControlLabel,
    List,
    ListItemButton,
    ListItemText,
} from "@mui/material";

import * as Lucide from "lucide-react";

import DOMPurify from "dompurify";

import { SVG } from "@svgdotjs/svg.js";
import "@svgdotjs/svg.select.js";
import "@svgdotjs/svg.resize.js";
import "@svgdotjs/svg.draggable.js";

import { getTemplate, updateTemplateContent } from "../../infra/api/templateApi";

const CANVAS_GREEN = "#eaf7ef";

// ---------- utils ----------
function clamp(n, min, max) {
    return Math.max(min, Math.min(max, n));
}

function sanitizeSvgForSave(raw) {
    return DOMPurify.sanitize(raw || "", {
        USE_PROFILES: { svg: true, svgFilters: true },
        ADD_TAGS: ["style"],
        ADD_ATTR: ["class", "style"],
    });
}

function safeNum(v, fallback = null) {
    if (v === "" || v == null) return fallback;
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
}

function uid(prefix = "el") {
    return `${prefix}_${Math.random().toString(16).slice(2)}_${Date.now().toString(16)}`;
}

function pickClickableSvgNode(target) {
    if (!target) return null;
    let el = target;

    while (el && el.nodeName) {
        const name = String(el.nodeName).toLowerCase();

        if (["defs", "style", "metadata", "title", "desc"].includes(name)) return null;
        if (name === "svg") return null;

        if (el.ownerSVGElement) return el;
        el = el.parentNode;
    }
    return null;
}

function isLocked(node) {
    return String(node?.getAttribute?.("data-locked") || "0") === "1";
}
function isHidden(node) {
    return String(node?.getAttribute?.("data-hidden") || "0") === "1";
}
function setLocked(node, locked) {
    if (!node) return;
    node.setAttribute("data-locked", locked ? "1" : "0");
}
function setHidden(node, hidden) {
    if (!node) return;
    node.setAttribute("data-hidden", hidden ? "1" : "0");
    node.style.display = hidden ? "none" : "";
}

// ---------- page ----------
export default function TemplateEditorPage() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);

    const [tpl, setTpl] = React.useState(null);

    const [showGrid, setShowGrid] = React.useState(true);
    const [snapGrid, setSnapGrid] = React.useState(true);

    const [zoom, setZoom] = React.useState(1);
    const [pan, setPan] = React.useState({ x: 0, y: 0 });

    // refs espelho (para não recriar callbacks e nem refazer fetch)
    const zoomRef = React.useRef(1);
    const panRef = React.useRef({ x: 0, y: 0 });
    const showGridRef = React.useRef(true);
    const snapGridRef = React.useRef(true);

    React.useEffect(() => { zoomRef.current = zoom; }, [zoom]);
    React.useEffect(() => { panRef.current = pan; }, [pan]);
    React.useEffect(() => { showGridRef.current = showGrid; }, [showGrid]);
    React.useEffect(() => { snapGridRef.current = snapGrid; }, [snapGrid]);

    // selection
    const [selectedIds, setSelectedIds] = React.useState([]);
    const selectedIdsRef = React.useRef([]);
    const [activeId, setActiveId] = React.useState(null);

    // inspector props
    const [props, setProps] = React.useState({
        id: "",
        type: "",
        x: "",
        y: "",
        w: "",
        h: "",
        fill: "",
        stroke: "",
        strokeWidth: "",
        opacity: "",
        fontFamily: "",
        fontSize: "",
        text: "",
    });

    // layers
    const [layers, setLayers] = React.useState([]);

    // undo/redo
    const historyRef = React.useRef({ undo: [], redo: [], max: 30 });

    // svg refs
    const hostRef = React.useRef(null);
    const drawRef = React.useRef(null);
    const viewportRef = React.useRef(null);
    const pageRef = React.useRef(null);
    const gridRef = React.useRef(null);

    // interaction refs
    const fileInputRef = React.useRef(null);
    const boxSelRef = React.useRef({ active: false, start: null, rect: null });
    const spaceDownRef = React.useRef(false);
    const panDragRef = React.useRef({ active: false, start: null, pan0: null });
    const rafRef = React.useRef(0);

    // ---------- helpers ----------
    const syncSelectedIds = React.useCallback((ids) => {
        const next = Array.isArray(ids) ? ids : [];
        selectedIdsRef.current = next;
        setSelectedIds(next);
        setActiveId(next[next.length - 1] || null);
    }, []);

    const getNodeById = React.useCallback((elId) => {
        const draw = drawRef.current;
        if (!draw || !elId) return null;
        return draw.node?.querySelector?.(`#${CSS.escape(elId)}`) || null;
    }, []);

    const applyViewportTransform = React.useCallback((z, p) => {
        const viewport = viewportRef.current;
        if (!viewport) return;
        viewport.transform({
            translateX: p.x,
            translateY: p.y,
            scale: z,
        });
    }, []);

    const snapshotForUndo = React.useCallback(() => {
        const draw = drawRef.current;
        if (!draw) return;

        const svg = draw.svg();
        const h = historyRef.current;

        h.undo.push(svg);
        if (h.undo.length > h.max) h.undo.shift();
        h.redo = [];
    }, []);

    const rebuildLayers = React.useCallback(() => {
        const page = pageRef.current;
        if (!page) return;

        const nodes = Array.from(page.node.children || [])
            .filter((n) => n && n.nodeName)
            .filter((n) => !["defs", "style", "metadata", "title", "desc"].includes(String(n.nodeName).toLowerCase()));

        const list = nodes
            .map((n) => {
                const lid = n.getAttribute("id") || "";
                if (!lid) return null;
                const type = String(n.nodeName).toLowerCase();
                const hidden = isHidden(n);
                const locked = isLocked(n);
                const label = n.getAttribute("data-label") || `${type}#${lid}`;
                return { id: lid, type, label, hidden, locked };
            })
            .filter(Boolean);

        setLayers(list);
    }, []);

    const updatePropsFromActive = React.useCallback(() => {
        if (!activeId) {
            setProps({
                id: "",
                type: "",
                x: "",
                y: "",
                w: "",
                h: "",
                fill: "",
                stroke: "",
                strokeWidth: "",
                opacity: "",
                fontFamily: "",
                fontSize: "",
                text: "",
            });
            return;
        }

        const dom = getNodeById(activeId);
        if (!dom) return;

        const el = SVG(dom);
        const type = String(el.type || dom.nodeName || "").toLowerCase();

        let bbox = null;
        try { bbox = el.bbox(); } catch {}

        const fill = el.attr("fill") ?? "";
        const stroke = el.attr("stroke") ?? "";
        const strokeWidth = el.attr("stroke-width") ?? "";
        const opacity = el.attr("opacity") ?? "";

        let x = el.attr("x");
        let y = el.attr("y");
        let w = el.attr("width");
        let h = el.attr("height");

        if ((x == null || x === "") && bbox) x = bbox.x;
        if ((y == null || y === "") && bbox) y = bbox.y;
        if ((w == null || w === "") && bbox) w = bbox.width;
        if ((h == null || h === "") && bbox) h = bbox.height;

        let text = "";
        if (type === "text") {
            try { text = el.text() || ""; } catch {}
        }

        const fontFamily = el.attr("font-family") ?? "";
        const fontSize = el.attr("font-size") ?? "";

        setProps({
            id: activeId,
            type,
            x: x ?? "",
            y: y ?? "",
            w: w ?? "",
            h: h ?? "",
            fill,
            stroke,
            strokeWidth,
            opacity,
            fontFamily,
            fontSize,
            text,
        });
    }, [activeId, getNodeById]);

    React.useEffect(() => {
        updatePropsFromActive();
    }, [activeId, updatePropsFromActive]);

    // ---------- selection/actions (definidas antes de bind events) ----------
    const deselectAll = React.useCallback(() => {
        const ids = selectedIdsRef.current;
        ids.forEach((sid) => {
            const dom = getNodeById(sid);
            if (!dom) return;
            try { SVG(dom).select(false); } catch {}
        });
        syncSelectedIds([]);
    }, [getNodeById, syncSelectedIds]);

    const bindDragResizeFor = React.useCallback((dom) => {
        if (!dom) return;
        if (isHidden(dom) || isLocked(dom)) return;

        const el = SVG(dom);

        // evita bind duplicado
        if (dom.__boundDragResize) return;
        dom.__boundDragResize = true;

        try { el.draggable(); } catch {}
        try {
            const type = String(dom.nodeName).toLowerCase();
            if (["rect", "image", "text", "circle", "ellipse", "line", "polygon", "polyline"].includes(type)) {
                el.resize();
            }
        } catch {}

        el.on("dragend", () => {
            // snap opcional
            if (snapGridRef.current) {
                try {
                    const b = el.bbox();
                    const step = 1; // 1mm
                    const nx = Math.round(b.x / step) * step;
                    const ny = Math.round(b.y / step) * step;

                    const t = String(el.type).toLowerCase();
                    if (["rect", "image", "text"].includes(t)) el.attr({ x: nx, y: ny });
                    else el.dmove(nx - b.x, ny - b.y);
                } catch {}
            }

            snapshotForUndo();
            rebuildLayers();
            updatePropsFromActive();
        });

        el.on("resizedone", () => {
            snapshotForUndo();
            rebuildLayers();
            updatePropsFromActive();
        });
    }, [rebuildLayers, snapshotForUndo, updatePropsFromActive]);

    const selectOne = React.useCallback((sid, additive) => {
        if (!sid) return;
        const dom = getNodeById(sid);
        if (!dom) return;
        if (isHidden(dom) || isLocked(dom)) return;

        const current = selectedIdsRef.current.slice();

        if (!additive) deselectAll();

        if (additive && current.includes(sid)) {
            try { SVG(dom).select(false); } catch {}
            syncSelectedIds(current.filter((x) => x !== sid));
            return;
        }

        try { SVG(dom).select(); } catch {}
        bindDragResizeFor(dom);

        syncSelectedIds(additive ? [...current, sid] : [sid]);
    }, [bindDragResizeFor, deselectAll, getNodeById, syncSelectedIds]);

    // ✅ não dá erro de "before initialization" porque está definido antes do effect que usa
    const deleteSelected = React.useCallback(() => {
        const page = pageRef.current;
        if (!page) return;

        const ids = selectedIdsRef.current.slice();
        if (!ids.length) return;

        snapshotForUndo();

        ids.forEach((sid) => {
            const dom = getNodeById(sid);
            if (!dom) return;
            if (isLocked(dom)) return;
            try { SVG(dom).remove(); } catch {}
        });

        syncSelectedIds([]);
        rebuildLayers();
    }, [getNodeById, rebuildLayers, snapshotForUndo, syncSelectedIds]);

    // ---------- coordinates ----------
    const eventPointToSvg = React.useCallback((evt) => {
        const draw = drawRef.current;
        if (!draw) return { x: 0, y: 0 };

        const svg = draw.node;
        const pt = svg.createSVGPoint();
        pt.x = evt.clientX;
        pt.y = evt.clientY;

        const ctm = svg.getScreenCTM();
        if (!ctm) return { x: 0, y: 0 };

        const p = pt.matrixTransform(ctm.inverse());

        const z = zoomRef.current;
        const panNow = panRef.current;

        return { x: (p.x - panNow.x) / z, y: (p.y - panNow.y) / z };
    }, []);

    // ---------- box select ----------
    const startBoxSelect = React.useCallback((pt) => {
        const viewport = viewportRef.current;
        if (!viewport) return;

        const box = boxSelRef.current;
        if (box.rect) {
            try { box.rect.remove(); } catch {}
        }

        box.active = true;
        box.start = pt;
        box.rect = viewport
            .rect(0, 0)
            .fill({ color: "#2563EB", opacity: 0.08 })
            .stroke({ color: "#2563EB", width: 0.3 })
            .attr({ "pointer-events": "none" });
    }, []);

    const updateBoxSelect = React.useCallback((pt) => {
        const box = boxSelRef.current;
        if (!box.active || !box.start || !box.rect) return;

        const x = Math.min(box.start.x, pt.x);
        const y = Math.min(box.start.y, pt.y);
        const w = Math.abs(box.start.x - pt.x);
        const h = Math.abs(box.start.y - pt.y);

        try { box.rect.move(x, y).size(w, h); } catch {}
    }, []);

    const endBoxSelect = React.useCallback((additive) => {
        const box = boxSelRef.current;
        if (!box.active || !box.rect) return;

        let b = null;
        try { b = box.rect.bbox(); } catch {}

        try { box.rect.remove(); } catch {}

        box.active = false;
        box.start = null;
        box.rect = null;

        if (!b) return;

        const page = pageRef.current;
        if (!page) return;

        const hit = [];
        const nodes = Array.from(page.node.children || []);

        for (const n of nodes) {
            if (!n.getAttribute) continue;
            if (isHidden(n) || isLocked(n)) continue;

            try {
                const bb = SVG(n).bbox();
                const inside = bb.x >= b.x && bb.y >= b.y && bb.x2 <= b.x + b.width && bb.y2 <= b.y + b.height;
                if (inside) hit.push(n.getAttribute("id"));
            } catch {}
        }

        if (!additive) deselectAll();

        const current = additive ? selectedIdsRef.current.slice() : [];
        const next = [...new Set([...current, ...hit])];

        next.forEach((sid) => {
            const dom = getNodeById(sid);
            if (!dom) return;
            try { SVG(dom).select(); } catch {}
            bindDragResizeFor(dom);
        });

        syncSelectedIds(next);
    }, [bindDragResizeFor, deselectAll, getNodeById, syncSelectedIds]);

    // ---------- doc builders ----------
    const buildNewA4 = React.useCallback(() => {
        return `<svg xmlns="http://www.w3.org/2000/svg" width="210mm" height="297mm" viewBox="0 0 210 297">
  <style>
    text { font-family: Arial, sans-serif; fill: #111827; }
  </style>
  <rect id="page_bg" x="0" y="0" width="210" height="297" fill="#ffffff" data-locked="1" data-label="Page Background"/>
  <text id="${uid("title")}" x="16" y="24" font-size="8" data-label="Title">Novo template SVG</text>
</svg>`;
    }, []);

    // ---------- load svg (estável; lê refs) ----------
    const loadSvg = React.useCallback((raw) => {
        const rawStr = raw || "";
        const host = hostRef.current;
        if (!host) return;

        // reset dom & state
        host.innerHTML = "";
        syncSelectedIds([]);

        const draw = SVG().addTo(host).size("100%", "100%");
        const viewport = draw.group().id("viewport");
        const grid = viewport.group().id("grid");
        const page = viewport.group().id("page");

        drawRef.current = draw;
        viewportRef.current = viewport;
        pageRef.current = page;
        gridRef.current = grid;

        const tmp = document.createElement("div");
        tmp.innerHTML = rawStr;
        const svgEl = tmp.querySelector("svg");

        const viewBox = svgEl?.getAttribute("viewBox") || "0 0 210 297";
        draw.viewbox(viewBox);

        // injeta <style> primeiro (evita tudo preto se classes dependem disso)
        const styleEl = svgEl?.querySelector("style");
        if (styleEl) page.node.insertAdjacentHTML("afterbegin", styleEl.outerHTML);

        if (svgEl) page.node.insertAdjacentHTML("beforeend", svgEl.innerHTML || "");
        else page.node.insertAdjacentHTML("beforeend", rawStr);

        // garante ids
        const nodes = Array.from(page.node.querySelectorAll("*"));
        nodes.forEach((n) => {
            const tag = String(n.nodeName || "").toLowerCase();
            if (["defs", "style", "metadata", "title", "desc"].includes(tag)) return;
            if (!n.getAttribute("id")) n.setAttribute("id", uid("node"));
            if (isHidden(n)) setHidden(n, true);
        });

        // trava background
        const bg = page.node.querySelector("#page_bg");
        if (bg) setLocked(bg, true);

        // grid
        const drawGrid = () => {
            grid.clear();
            if (!showGridRef.current) return;

            const step = 5; // 5mm
            const vb = draw.viewbox();
            const w = vb.width;
            const h = vb.height;

            for (let x = 0; x <= w; x += step) grid.line(x, 0, x, h).stroke({ width: 0.15, color: "#dbe7e0" });
            for (let y = 0; y <= h; y += step) grid.line(0, y, w, y).stroke({ width: 0.15, color: "#dbe7e0" });
        };
        drawGrid();

        // aplica transform atual
        applyViewportTransform(zoomRef.current, panRef.current);

        // history inicial
        historyRef.current.undo = [draw.svg()];
        historyRef.current.redo = [];

        rebuildLayers();
    }, [applyViewportTransform, rebuildLayers, syncSelectedIds]);

    const exportSvgString = React.useCallback(() => {
        const draw = drawRef.current;
        const page = pageRef.current;
        if (!draw || !page) return "";

        const vb = draw.viewbox();
        const inner = page.node.innerHTML || "";

        return `<svg xmlns="http://www.w3.org/2000/svg" width="210mm" height="297mm" viewBox="${vb.x} ${vb.y} ${vb.width} ${vb.height}">
${inner}
</svg>`;
    }, []);

    // ---------- history ----------
    const undo = React.useCallback(() => {
        const h = historyRef.current;
        if (h.undo.length <= 1) return;

        const cur = h.undo.pop();
        h.redo.push(cur);

        const prev = h.undo[h.undo.length - 1];
        if (!prev) return;

        loadSvg(prev);
    }, [loadSvg]);

    const redo = React.useCallback(() => {
        const h = historyRef.current;
        if (!h.redo.length) return;

        const next = h.redo.pop();
        if (!next) return;

        h.undo.push(next);
        loadSvg(next);
    }, [loadSvg]);

    // ---------- create elements ----------
    const createRect = React.useCallback(() => {
        const page = pageRef.current;
        if (!page) return;

        snapshotForUndo();
        const el = page.rect(40, 20).move(20, 40).fill("#ffffff").stroke({ color: "#111827", width: 0.5 });
        el.id(uid("rect")).attr({ "data-label": "Rect" });

        rebuildLayers();
    }, [rebuildLayers, snapshotForUndo]);

    const createCircle = React.useCallback(() => {
        const page = pageRef.current;
        if (!page) return;

        snapshotForUndo();
        const el = page.circle(18).move(20, 70).fill("#dbeafe").stroke({ color: "#2563EB", width: 0.5 });
        el.id(uid("circle")).attr({ "data-label": "Circle" });

        rebuildLayers();
    }, [rebuildLayers, snapshotForUndo]);

    const createLine = React.useCallback(() => {
        const page = pageRef.current;
        if (!page) return;

        snapshotForUndo();
        const el = page.line(20, 100, 120, 100).stroke({ color: "#111827", width: 0.6 });
        el.id(uid("line")).attr({ "data-label": "Line" });

        rebuildLayers();
    }, [rebuildLayers, snapshotForUndo]);

    const createText = React.useCallback(() => {
        const page = pageRef.current;
        if (!page) return;

        snapshotForUndo();
        const el = page.text("Texto").move(20, 120).fill("#111827").font({ size: 8, family: "Arial" });
        el.id(uid("text")).attr({ "data-label": "Text" });

        rebuildLayers();
    }, [rebuildLayers, snapshotForUndo]);

    const applyProps = React.useCallback(() => {
        const sid = activeId;
        if (!sid) return;

        const dom = getNodeById(sid);
        if (!dom) return;
        if (isLocked(dom)) return;

        snapshotForUndo();

        const el = SVG(dom);
        const type = String(el.type || dom.nodeName || "").toLowerCase();

        if (props.fill !== "") el.attr({ fill: props.fill });
        if (props.stroke !== "") el.attr({ stroke: props.stroke });
        if (props.strokeWidth !== "") el.attr({ "stroke-width": props.strokeWidth });
        if (props.opacity !== "") el.attr({ opacity: props.opacity });

        const x = safeNum(props.x);
        const y = safeNum(props.y);
        const w = safeNum(props.w);
        const h = safeNum(props.h);

        const snap = (v) => {
            if (!snapGridRef.current) return v;
            const step = 1;
            return Math.round(v / step) * step;
        };

        if (type === "rect" || type === "image") {
            if (x != null) el.attr({ x: snap(x) });
            if (y != null) el.attr({ y: snap(y) });
            if (w != null) el.attr({ width: Math.max(0, w) });
            if (h != null) el.attr({ height: Math.max(0, h) });
        }

        if (type === "text") {
            if (x != null) el.attr({ x: snap(x) });
            if (y != null) el.attr({ y: snap(y) });
            if (props.fontFamily !== "") el.attr({ "font-family": props.fontFamily });
            if (props.fontSize !== "") el.attr({ "font-size": props.fontSize });
            el.text(props.text || "");
        }

        rebuildLayers();
        updatePropsFromActive();
    }, [
        activeId,
        getNodeById,
        props.fill,
        props.stroke,
        props.strokeWidth,
        props.opacity,
        props.x,
        props.y,
        props.w,
        props.h,
        props.fontFamily,
        props.fontSize,
        props.text,
        rebuildLayers,
        snapshotForUndo,
        updatePropsFromActive,
    ]);

    // ---------- layers ops ----------
    const layerSelect = React.useCallback((layerId) => {
        selectOne(layerId, false);
    }, [selectOne]);

    const layerToggleHidden = React.useCallback((layerId) => {
        const dom = getNodeById(layerId);
        if (!dom) return;

        snapshotForUndo();
        const next = !isHidden(dom);
        setHidden(dom, next);

        rebuildLayers();
        if (selectedIdsRef.current.includes(layerId) && next) deselectAll();
    }, [deselectAll, getNodeById, rebuildLayers, snapshotForUndo]);

    const layerToggleLock = React.useCallback((layerId) => {
        const dom = getNodeById(layerId);
        if (!dom) return;

        snapshotForUndo();
        const next = !isLocked(dom);
        setLocked(dom, next);

        rebuildLayers();
        if (selectedIdsRef.current.includes(layerId) && next) deselectAll();
    }, [deselectAll, getNodeById, rebuildLayers, snapshotForUndo]);

    const layerMove = React.useCallback((layerId, dir) => {
        const dom = getNodeById(layerId);
        if (!dom) return;

        snapshotForUndo();
        const el = SVG(dom);
        try {
            if (dir === "up") el.forward();
            else el.backward();
        } catch {}

        rebuildLayers();
    }, [getNodeById, rebuildLayers, snapshotForUndo]);

    // ---------- grid toggle ----------
    const toggleGrid = React.useCallback((v) => {
        setShowGrid(v);

        // re-render grid sem destruir tudo: recarrega o svg atual (rápido e simples)
        const draw = drawRef.current;
        if (!draw) return;
        loadSvg(draw.svg());
    }, [loadSvg]);

    // ---------- doc actions ----------
    const newDoc = React.useCallback(() => {
        const z = 1;
        const p = { x: 0, y: 0 };

        setZoom(z);
        setPan(p);
        zoomRef.current = z;
        panRef.current = p;

        loadSvg(buildNewA4());
        applyViewportTransform(z, p);
    }, [applyViewportTransform, buildNewA4, loadSvg]);

    const onPickFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;

        const raw = await file.text();

        const z = 1;
        const p = { x: 0, y: 0 };
        setZoom(z);
        setPan(p);
        zoomRef.current = z;
        panRef.current = p;

        loadSvg(raw);
        applyViewportTransform(z, p);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const svgRaw = exportSvgString();
            const svgSafe = sanitizeSvgForSave(svgRaw);

            const updated = await updateTemplateContent(id, { contentSvg: svgSafe });
            setTpl(updated);

            const newSvg = updated?.contentSvg || updated?.content || svgRaw;
            loadSvg(newSvg);
        } finally {
            setSaving(false);
        }
    };

    // ---------- bind DOM events (1x por loadSvg, sem loop) ----------
    React.useEffect(() => {
        const host = hostRef.current;
        const draw = drawRef.current;
        const page = pageRef.current;
        if (!host || !draw || !page) return;

        if (host.__cleanup) host.__cleanup();

        const onClick = (e) => {
            if (boxSelRef.current.active) return;

            const dom = pickClickableSvgNode(e.target);

            if (!dom) {
                deselectAll();
                return;
            }

            if (!page.node.contains(dom)) return;

            const sid = dom.getAttribute("id");
            if (!sid) return;

            selectOne(sid, e.shiftKey === true);
        };

        const onMouseDown = (e) => {
            if (e.button !== 0) return;

            if (spaceDownRef.current) {
                panDragRef.current.active = true;
                panDragRef.current.start = { x: e.clientX, y: e.clientY };
                panDragRef.current.pan0 = { ...panRef.current };
                host.style.cursor = "grabbing";
                e.preventDefault();
                return;
            }

            const dom = pickClickableSvgNode(e.target);
            if (dom && page.node.contains(dom)) return;

            startBoxSelect(eventPointToSvg(e));
        };

        const onMouseMove = (e) => {
            // pan drag (sem setState a cada pixel)
            if (panDragRef.current.active && panDragRef.current.start) {
                const dx = e.clientX - panDragRef.current.start.x;
                const dy = e.clientY - panDragRef.current.start.y;
                const next = { x: panDragRef.current.pan0.x + dx, y: panDragRef.current.pan0.y + dy };

                panRef.current = next;

                // throttle com rAF
                if (!rafRef.current) {
                    rafRef.current = requestAnimationFrame(() => {
                        rafRef.current = 0;
                        applyViewportTransform(zoomRef.current, panRef.current);
                    });
                }
                return;
            }

            if (!boxSelRef.current.active) return;
            updateBoxSelect(eventPointToSvg(e));
        };

        const onMouseUp = (e) => {
            if (panDragRef.current.active) {
                panDragRef.current.active = false;
                panDragRef.current.start = null;
                panDragRef.current.pan0 = null;

                host.style.cursor = spaceDownRef.current ? "grab" : "default";

                // commit state
                setPan(panRef.current);
                return;
            }

            if (boxSelRef.current.active) endBoxSelect(e.shiftKey === true);
        };

        const onWheel = (e) => {
            e.preventDefault();

            const factor = e.deltaY > 0 ? 0.92 : 1.08;
            const nextZoom = clamp(Math.round(zoomRef.current * factor * 100) / 100, 0.2, 6.0);

            zoomRef.current = nextZoom;
            setZoom(nextZoom);

            applyViewportTransform(nextZoom, panRef.current);
        };

        const onKeyDown = (e) => {
            if (e.code === "Space") {
                e.preventDefault();
                spaceDownRef.current = true;
                host.style.cursor = "grab";
            }

            if (e.key === "Delete" || e.key === "Backspace") {
                if (selectedIdsRef.current.length) {
                    e.preventDefault();
                    deleteSelected();
                }
            }

            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") {
                e.preventDefault();
                undo();
            }
            if ((e.ctrlKey || e.metaKey) && (e.key.toLowerCase() === "y" || (e.shiftKey && e.key.toLowerCase() === "z"))) {
                e.preventDefault();
                redo();
            }
        };

        const onKeyUp = (e) => {
            if (e.code === "Space") {
                e.preventDefault();
                spaceDownRef.current = false;
                host.style.cursor = "default";

                panDragRef.current.active = false;
                panDragRef.current.start = null;
                panDragRef.current.pan0 = null;
            }
        };

        host.addEventListener("click", onClick);
        host.addEventListener("mousedown", onMouseDown);
        host.addEventListener("mousemove", onMouseMove);
        host.addEventListener("mouseup", onMouseUp);
        host.addEventListener("wheel", onWheel, { passive: false });

        window.addEventListener("keydown", onKeyDown);
        window.addEventListener("keyup", onKeyUp);

        host.__cleanup = () => {
            host.removeEventListener("click", onClick);
            host.removeEventListener("mousedown", onMouseDown);
            host.removeEventListener("mousemove", onMouseMove);
            host.removeEventListener("mouseup", onMouseUp);
            host.removeEventListener("wheel", onWheel);

            window.removeEventListener("keydown", onKeyDown);
            window.removeEventListener("keyup", onKeyUp);
        };

        return () => {
            try { host.__cleanup?.(); } catch {}
        };
    }, [
        applyViewportTransform,
        deleteSelected,
        deselectAll,
        endBoxSelect,
        eventPointToSvg,
        redo,
        selectOne,
        startBoxSelect,
        undo,
        updateBoxSelect,
    ]);

    // ---------- load template (fetch 1x por id; SEM loadSvg deps) ----------
    React.useEffect(() => {
        let alive = true;

        (async () => {
            setLoading(true);
            try {
                const data = await getTemplate(id);
                if (!alive) return;

                setTpl(data);

                const rawSvg = data?.contentSvg || data?.content || "";
                if (rawSvg) loadSvg(rawSvg);
                else newDoc();
            } finally {
                if (alive) setLoading(false);
            }
        })();

        return () => {
            alive = false;
            try { hostRef.current?.__cleanup?.(); } catch {}
        };
    }, [id, loadSvg, newDoc]);

    // ---------- zoom buttons ----------
    const onZoomIn = () => {
        const next = clamp(Math.round((zoomRef.current + 0.1) * 10) / 10, 0.2, 6.0);
        zoomRef.current = next;
        setZoom(next);
        applyViewportTransform(next, panRef.current);
    };
    const onZoomOut = () => {
        const next = clamp(Math.round((zoomRef.current - 0.1) * 10) / 10, 0.2, 6.0);
        zoomRef.current = next;
        setZoom(next);
        applyViewportTransform(next, panRef.current);
    };
    const onZoomReset = () => {
        const z = 1;
        const p = { x: 0, y: 0 };
        zoomRef.current = z;
        panRef.current = p;
        setZoom(z);
        setPan(p);
        applyViewportTransform(z, p);
    };

    // ---------- UI ----------
    if (loading) {
        return (
            <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ height: "100dvh", display: "flex", flexDirection: "column", bgcolor: CANVAS_GREEN }}>
            {/* CSS de seleção/handles (não fica “tudo preto”) */}
            <style>
                {`
          .svg_select_boundingRect {
            stroke: #2563EB !important;
            stroke-width: 1 !important;
            stroke-dasharray: 4 2 !important;
            fill: rgba(37, 99, 235, 0.06) !important;
          }
          .svg_select_points, .svg_resize_points {
            fill: #2563EB !important;
            stroke: #ffffff !important;
            stroke-width: 1.5 !important;
          }
        `}
            </style>

            {/* HEADER */}
            <Box sx={{ p: 2, borderBottom: "1px solid rgba(0,0,0,.08)" }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap">
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: "-0.02em" }}>
                            {tpl?.name || "Template"}{" "}
                            <Typography component="span" color="text.secondary" sx={{ fontWeight: 500 }}>
                                — SVG Editor
                            </Typography>
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                            Clique seleciona • Shift multi • Arrastar no vazio = box select • Wheel zoom • Space+drag pan • Delete remove
                        </Typography>
                    </Box>

                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Tooltip title="Undo (Ctrl+Z)">
                            <IconButton onClick={undo}><Lucide.Undo2 /></IconButton>
                        </Tooltip>
                        <Tooltip title="Redo (Ctrl+Y)">
                            <IconButton onClick={redo}><Lucide.Redo2 /></IconButton>
                        </Tooltip>

                        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

                        <Tooltip title="Diminuir zoom">
                            <IconButton onClick={onZoomOut}><Lucide.ZoomOut /></IconButton>
                        </Tooltip>
                        <Tooltip title="Restaurar zoom">
                            <IconButton onClick={onZoomReset}><Lucide.RotateCcw /></IconButton>
                        </Tooltip>
                        <Tooltip title="Aumentar zoom">
                            <IconButton onClick={onZoomIn}><Lucide.ZoomIn /></IconButton>
                        </Tooltip>

                        <Chip size="small" variant="outlined" label={`${Math.round(zoom * 100)}%`} sx={{ bgcolor: "rgba(255,255,255,.65)" }} />

                        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

                        <Button variant="outlined" startIcon={<Lucide.FileUp size={18} />} onClick={() => fileInputRef.current?.click()}>
                            Upload SVG
                        </Button>
                        <input ref={fileInputRef} type="file" accept=".svg,image/svg+xml" style={{ display: "none" }} onChange={onPickFile} />

                        <Button variant="outlined" startIcon={<Lucide.SquarePlus size={18} />} onClick={newDoc}>
                            Novo A4
                        </Button>

                        <Button variant="outlined" onClick={() => navigate(-1)} startIcon={<Lucide.ArrowLeft size={18} />}>
                            Voltar
                        </Button>

                        <Button variant="contained" onClick={handleSave} startIcon={<Lucide.Save size={18} />} disabled={saving}>
                            {saving ? "Salvando..." : "Salvar"}
                        </Button>
                    </Stack>
                </Stack>

                <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1, flexWrap: "wrap" }}>
                    <FormControlLabel control={<Switch checked={showGrid} onChange={(e) => toggleGrid(e.target.checked)} />} label="Grade" />
                    <FormControlLabel control={<Switch checked={snapGrid} onChange={(e) => setSnapGrid(e.target.checked)} />} label="Snap" />

                    <Divider orientation="vertical" flexItem />

                    <Button size="small" variant="outlined" startIcon={<Lucide.Square size={18} />} onClick={createRect}>Retângulo</Button>
                    <Button size="small" variant="outlined" startIcon={<Lucide.Circle size={18} />} onClick={createCircle}>Círculo</Button>
                    <Button size="small" variant="outlined" startIcon={<Lucide.Minus size={18} />} onClick={createLine}>Linha</Button>
                    <Button size="small" variant="outlined" startIcon={<Lucide.Type size={18} />} onClick={createText}>Texto</Button>

                    <Divider orientation="vertical" flexItem />

                    <Button
                        size="small"
                        color="error"
                        variant="outlined"
                        startIcon={<Lucide.Trash2 size={18} />}
                        onClick={deleteSelected}
                        disabled={!selectedIds.length}
                    >
                        Remover
                    </Button>
                </Stack>
            </Box>

            {/* BODY */}
            <Stack direction="row" sx={{ flex: 1, overflow: "hidden" }}>
                {/* CANVAS */}
                <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
                    <Paper
                        elevation={0}
                        sx={{
                            width: "210mm",
                            height: "297mm",
                            mx: "auto",
                            borderRadius: 2.5,
                            border: "1px solid rgba(0,0,0,.10)",
                            boxShadow: "0 10px 25px rgba(16,24,40,.08)",
                            bgcolor: "#fff",
                        }}
                    >
                        <Box ref={hostRef} sx={{ width: "100%", height: "100%", cursor: "default", userSelect: "none" }} />
                    </Paper>
                </Box>

                {/* SIDEBAR */}
                <Box
                    sx={{
                        width: 420,
                        borderLeft: "1px solid rgba(0,0,0,.08)",
                        bgcolor: "rgba(255,255,255,.85)",
                        p: 2,
                        overflow: "auto",
                    }}
                >
                    {/* PROPS */}
                    <Typography sx={{ fontWeight: 900 }}>Propriedades</Typography>

                    {!activeId ? (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Selecione um elemento para editar.
                        </Typography>
                    ) : (
                        <>
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                                Ativo: <b>{props.type}</b> #{props.id}
                            </Typography>

                            <Stack direction="row" gap={1} sx={{ mt: 2 }}>
                                <TextField label="x" value={props.x} onChange={(e) => setProps((p) => ({ ...p, x: e.target.value }))} fullWidth />
                                <TextField label="y" value={props.y} onChange={(e) => setProps((p) => ({ ...p, y: e.target.value }))} fullWidth />
                            </Stack>

                            <Stack direction="row" gap={1} sx={{ mt: 2 }}>
                                <TextField label="w" value={props.w} onChange={(e) => setProps((p) => ({ ...p, w: e.target.value }))} fullWidth />
                                <TextField label="h" value={props.h} onChange={(e) => setProps((p) => ({ ...p, h: e.target.value }))} fullWidth />
                            </Stack>

                            <Divider sx={{ my: 2 }} />

                            <TextField label="fill" value={props.fill} onChange={(e) => setProps((p) => ({ ...p, fill: e.target.value }))} fullWidth />
                            <TextField label="stroke" value={props.stroke} onChange={(e) => setProps((p) => ({ ...p, stroke: e.target.value }))} fullWidth sx={{ mt: 2 }} />
                            <TextField label="strokeWidth" value={props.strokeWidth} onChange={(e) => setProps((p) => ({ ...p, strokeWidth: e.target.value }))} fullWidth sx={{ mt: 2 }} />
                            <TextField label="opacity" value={props.opacity} onChange={(e) => setProps((p) => ({ ...p, opacity: e.target.value }))} fullWidth sx={{ mt: 2 }} />

                            {props.type === "text" && (
                                <>
                                    <Divider sx={{ my: 2 }} />
                                    <TextField label="fontFamily" value={props.fontFamily} onChange={(e) => setProps((p) => ({ ...p, fontFamily: e.target.value }))} fullWidth />
                                    <TextField label="fontSize" value={props.fontSize} onChange={(e) => setProps((p) => ({ ...p, fontSize: e.target.value }))} fullWidth sx={{ mt: 2 }} />
                                    <TextField label="text" value={props.text} onChange={(e) => setProps((p) => ({ ...p, text: e.target.value }))} fullWidth multiline minRows={3} sx={{ mt: 2 }} />
                                </>
                            )}

                            <Stack direction="row" gap={1} sx={{ mt: 2 }}>
                                <Button variant="contained" fullWidth onClick={applyProps}>Aplicar</Button>
                                <Button variant="text" fullWidth color="error" onClick={deselectAll}>Desmarcar</Button>
                            </Stack>
                        </>
                    )}

                    <Divider sx={{ my: 2 }} />

                    {/* LAYERS */}
                    <Typography sx={{ fontWeight: 900 }}>Camadas</Typography>
                    <Typography variant="caption" color="text.secondary">
                        lock/hide • subir/descer
                    </Typography>

                    <List dense sx={{ mt: 1 }}>
                        {layers.map((l) => (
                            <ListItemButton
                                key={l.id}
                                selected={activeId === l.id}
                                onClick={() => layerSelect(l.id)}
                                sx={{ borderRadius: 1 }}
                            >
                                <ListItemText
                                    primary={l.label}
                                    secondary={`${l.type} • #${l.id}`}
                                    primaryTypographyProps={{ noWrap: true }}
                                />

                                <Tooltip title={l.hidden ? "Mostrar" : "Ocultar"}>
                                    <IconButton size="small" onClick={(e) => (e.stopPropagation(), layerToggleHidden(l.id))}>
                                        {l.hidden ? <Lucide.EyeOff size={18} /> : <Lucide.Eye size={18} />}
                                    </IconButton>
                                </Tooltip>

                                <Tooltip title={l.locked ? "Destravar" : "Travar"}>
                                    <IconButton size="small" onClick={(e) => (e.stopPropagation(), layerToggleLock(l.id))}>
                                        {l.locked ? <Lucide.Lock size={18} /> : <Lucide.LockOpen size={18} />}
                                    </IconButton>
                                </Tooltip>

                                <Tooltip title="Subir">
                                    <IconButton size="small" onClick={(e) => (e.stopPropagation(), layerMove(l.id, "up"))}>
                                        <Lucide.ArrowUp size={18} />
                                    </IconButton>
                                </Tooltip>

                                <Tooltip title="Descer">
                                    <IconButton size="small" onClick={(e) => (e.stopPropagation(), layerMove(l.id, "down"))}>
                                        <Lucide.ArrowDown size={18} />
                                    </IconButton>
                                </Tooltip>
                            </ListItemButton>
                        ))}
                    </List>

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="caption" color="text.secondary">
                        Dica: use <b>id</b> significativo (ex.: <code>{`text id="clienteNome"`}</code>)
                    </Typography>
                </Box>
            </Stack>
        </Box>
    );
}
